import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LangProvider } from "../contexts/LangContext";
import { AboutPage } from "../pages/AboutPage";
import { buildCompleteHead } from "../utils/seo";

const allowedLanguages = ["oz", "uz", "ru", "en"];

export const Route = createFileRoute("/$lang/about")({
  head: ({ params }) => {
    const currentPath = `/${params.lang}/about`;
    const canonicalUrl = `https://lotin.uz${currentPath}`;
    const seoData = buildCompleteHead(
      params.lang as "oz" | "uz" | "ru",
      "home",
      canonicalUrl,
      currentPath,
      currentPath,
    );

    return {
      meta: [{ title: seoData.title }, ...seoData.meta],
      links: seoData.links,
    };
  },
  // Cast as any to satisfy older `beforeLoad` typing that expects `never`
  beforeLoad: ((ctx: { params: { lang: string } }) => {
    const { params } = ctx;
    if (!allowedLanguages.includes(params.lang)) {
      throw redirect({ to: "/about" });
    }
    if (params.lang === "oz") {
      throw redirect({ to: "/about" });
    }
  }) as any,
  component: About,
});

function About() {
  const { lang } = useParams({ from: "/$lang/about" });
  const { i18n } = useTranslation();

  useEffect(() => {
    const langMap: Record<string, string> = {
      oz: "uz-Latn", // Uzbek Latin (default)
      uz: "uz-Cyrl", // Uzbek Cyrillic
      ru: "ru", // Russian
      en: "en-US", // English
    };
    document.documentElement.lang = langMap[lang] || "uz-Latn";
    // Guard for SSR: only call changeLanguage if it exists
    if (i18n?.changeLanguage) {
      i18n.changeLanguage(lang);
    }

    // Set document title based on language
    const titleMap: Record<string, string> = {
      oz: "About — Lotin.uz",
      uz: "Ҳақида — Lotin.uz",
      ru: "О нас — Lotin.uz",
      en: "About — Lotin.uz",
    };
    document.title = titleMap[lang] || titleMap.oz;
  }, [lang, i18n]);

  const langMapForContext: Record<string, string> = {
    oz: "uz-Latn",
    uz: "uz-Cyrl",
    ru: "ru",
    en: "en-US",
  };

  return (
    <LangProvider lang={langMapForContext[lang] || "uz-Latn"}>
      <AboutPage lang={langMapForContext[lang]} />
    </LangProvider>
  );
}
